import { delay, splitArrayInChunks } from 'utils';
import { CancelToken, CancelTokenSource } from 'axios';
import axios, { throwAxiosError } from './request';
import {
  Aggregate,
  AggregateCategory,
  AggregateOption,
} from '../Types/Aggregates';

const endpoint = `/aggregates/`;
const batchSize = parseInt(
  process.env.REACT_APP_AGGREGATE_CALC_BATCH_SIZE || '10',
  10
);
const batchDelay = parseInt(
  process.env.REACT_APP_AGGREGATE_CALC_BATCH_DELAY_MS || '100',
  10
);

const pendingPromiseControllers: {
  [id: string | number]: CancelTokenSource;
} = {};

export interface PaginatedAggregateResponse {
  count: number;
  next: number;
  previous: number;
  results: AggregateOption[];
}

export const getAggregates = async (): Promise<PaginatedAggregateResponse | null> => {
  try {
    const { data } = await axios.get(`${endpoint}?page_size=500`);
    if (data) {
      return data as PaginatedAggregateResponse;
    }
  } catch (error) {
    throwAxiosError(error);
  }
  return null;
};

export const getAggregateHeaders = async (): Promise<
  AggregateCategory[] | null
> => {
  try {
    const { data } = await axios.get(`${endpoint}headers/`);
    if (data) {
      return data;
    }
  } catch (error) {
    throwAxiosError(error);
  }
  return null;
};

export const getAggregate = async (
  aggregateId: string
): Promise<Aggregate | null> => {
  try {
    const { data } = await axios.get(`${endpoint}${aggregateId}/`);
    if (data) {
      return data as Aggregate;
    }
  } catch (error) {
    throwAxiosError(error);
  }
  return null;
};

export const getAggregateDetails = async (
  aggregateId: string
): Promise<Aggregate | null> => {
  if (!aggregateId) {
    return null;
  }
  try {
    const { data } = await axios.get(`${endpoint}${aggregateId}/details/`);
    if (data) {
      return data as Aggregate;
    }
  } catch (error) {
    throwAxiosError(error);
  }
  return null;
};

export const getAggregateCategories = async (): Promise<
  AggregateCategory[] | null
> => {
  try {
    const { data } = await axios.get(`${endpoint}categories/`);
    if (data) {
      return data as AggregateCategory[];
    }
  } catch (error) {
    throwAxiosError(error);
  }
  return null;
};

export const getAggregateCalc = (
  aggregateId: string,
  params: { [key: string]: any },
  cancelToken: CancelToken
): Promise<{ url: string; data: Aggregate }> =>
  axios.put(`/calculator/${aggregateId}/`, { ...params }, { cancelToken });

const batchWithDelay = async (
  batch: Array<() => void>,
  d: number = 100
): Promise<void> => {
  await Promise.all(batch.map((func) => func()));
  await delay(d);
};

export const cancelAggregateRequests = () => {
  const keys = Object.keys(pendingPromiseControllers);
  keys.forEach((k) => {
    pendingPromiseControllers[k].cancel(
      'cancelled by aggregate service: cancelAggregateRequests'
    );
    delete pendingPromiseControllers[k];
  });
};

export const getAggregatesBatchRequest = (
  aggregateIds: string[],
  params: { [key: string]: any },
  onUpdate: (a: Aggregate) => void,
  onError: (id: string) => void
): (() => Promise<void>)[] => {
  // console.log('getAggregatesBatchRequest ran');
  const batches = splitArrayInChunks(aggregateIds, batchSize);
  const promiseBatches: any[] = [];

  // push promises into each array index in batch
  for (let i = 0; i < batches.length; i += 1) {
    const promiseBatch = batches[i].map((id) => async () => {
      const source = axios.CancelToken.source(); // use the controller to cancel request
      pendingPromiseControllers[`${id}_${new Date().getTime()}`] = source;
      try {
        const { data } = await getAggregateCalc(id, params, source.token);
        if (data) {
          onUpdate(data);
        }
      } catch (err) {
        onError(id);
      }
    });
    promiseBatches.push(promiseBatch);
  }

  const batchFuncs = [];
  // this should run batches in sync, hence await inside foor loop
  for (let k = 0; k < promiseBatches.length; k += 1) {
    batchFuncs.push(() => batchWithDelay(promiseBatches[k], batchDelay));
  }

  return batchFuncs;
};
